import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { formsAPI } from '../../utils/crmApi';

const FormResponses = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subs, setSubs] = useState([]);
  const [form, setForm] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const load = async () => {
    try {
      setLoading(true);
      // Load form to get field labels
      const allForms = await formsAPI.getAll();
      const foundForm = (allForms.data?.data || []).find(f => f._id === id);
      setForm(foundForm || null);
      
      // Load submissions
      const { data } = await formsAPI.getSubmissions(id);
      setSubs(data.data || []);
    } catch (e) {
      console.error('Failed to load submissions', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);
  
  // Get field label by key
  const getFieldLabel = (key) => {
    if (!form) return key;
    for (const page of form.pages || []) {
      for (const el of page.elements || []) {
        if (el.key === key) {
          return el.label?.en || el.label || key;
        }
      }
    }
    return key;
  };
  
  // Format answer value for display
  const formatAnswer = (value) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') {
      if (Array.isArray(value)) return value.join(', ');
      return JSON.stringify(value);
    }
    const str = String(value);
    return str.length > 50 ? str.substring(0, 50) + '...' : str;
  };
  
  // Get preview of answers (first 3 fields)
  const getAnswerPreview = (answers) => {
    if (!answers || typeof answers !== 'object') return 'No answers';
    const keys = Object.keys(answers).slice(0, 3);
    if (keys.length === 0) return 'No answers';
    return keys.map(k => {
      const label = getFieldLabel(k);
      const value = formatAnswer(answers[k]);
      return `${label}: ${value}`;
    }).join(' • ');
  };
  
  // Filtered submissions
  const filteredSubs = useMemo(() => {
    return subs.filter(s => {
      // Status filter
      if (statusFilter !== 'all' && s.status !== statusFilter) return false;
      
      // Search filter (search in answers, respondent, etc.)
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const answerText = JSON.stringify(s.answers || {}).toLowerCase();
        const respondentText = `${s.respondent?.email || ''} ${s.respondent?.name || ''}`.toLowerCase();
        if (!answerText.includes(query) && !respondentText.includes(query)) {
          return false;
        }
      }
      return true;
    });
  }, [subs, statusFilter, searchQuery]);

  const downloadCsv = async () => {
    try {
      const res = await formsAPI.exportSubmissionsCsv(id);
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'submissions.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Failed to export CSV', e);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300';
      case 'reviewed': return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300';
      case 'converted': return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300';
      default: return 'bg-muted text-foreground';
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold text-foreground">Responses</h1>
        <div className="flex gap-2">
          <button onClick={() => navigate(`/forms/${id}/analytics`)} className="px-3 py-2 rounded-md border border-input bg-background text-foreground hover:bg-muted">Analytics</button>
          <button onClick={downloadCsv} className="px-3 py-2 rounded-md bg-muted text-foreground hover:bg-gray-300 dark:hover:bg-gray-700">Export CSV</button>
        </div>
      </div>
      
      {/* Filters */}
      <div className="mb-4 flex gap-4 items-center">
        <div className="flex-1 max-w-md">
          <input
            type="text"
            placeholder="Search answers, email, name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-2 focus:outline-blue-500 dark:focus:outline-blue-400"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-2 focus:outline-blue-500 dark:focus:outline-blue-400"
        >
          <option value="all">All Status</option>
          <option value="new">New</option>
          <option value="reviewed">Reviewed</option>
          <option value="converted">Converted</option>
        </select>
        <div className="text-sm text-muted-foreground">
          {filteredSubs.length} of {subs.length} submission{filteredSubs.length !== 1 ? 's' : ''}
        </div>
      </div>
      
      {loading ? (
        <div className="text-muted-foreground">Loading...</div>
      ) : (
        <div className="overflow-x-auto bg-card rounded-md shadow border border-border">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-muted">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Respondent</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Answers Preview</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Duration</th>
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-gray-200 dark:divide-gray-700">
              {filteredSubs.map(s => (
                <tr key={s._id} className="hover:bg-muted cursor-pointer" onClick={() => navigate(`/forms/${id}/responses/${s._id}`)}>
                  <td className="px-4 py-3 text-sm text-foreground whitespace-nowrap">
                    {new Date(s.createdAt).toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div>
                      {s.respondent?.name && <div className="font-medium text-foreground">{s.respondent.name}</div>}
                      {s.respondent?.email && <div className="text-muted-foreground text-xs">{s.respondent.email}</div>}
                      {!s.respondent?.name && !s.respondent?.email && <span className="text-muted-foreground">Anonymous</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground max-w-md">
                    <div className="truncate" title={getAnswerPreview(s.answers)}>
                      {getAnswerPreview(s.answers)}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(s.status)}`}>
                      {s.status || 'new'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">
                    {s.meta?.durationSec ? `${Math.floor(s.meta.durationSec / 60)}m ${s.meta.durationSec % 60}s` : '-'}
                  </td>
                </tr>
              ))}
              {filteredSubs.length === 0 && (
                <tr>
                  <td className="px-4 py-8 text-center text-muted-foreground" colSpan={5}>
                    {subs.length === 0 ? 'No submissions yet.' : 'No submissions match your filters.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default FormResponses;


